// Firestore access for the Payroll Financing tab (admin@almhr.com and
// nazim@almhr.com only — see firestore.rules). All maths is in
// payrollFinanceCalculator.js; this file loads, validates and writes.
import { addDoc, collection, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import { createExpense, rejectExpense } from "./financeCore/operatingExpenseService";
import {
  DEFAULT_MONTHLY_RATE, MOVEMENT_TYPES, PF_PORTALS, allocateFifo, isActive, portalLabel, round2,
  validateMovementAllocations, validateRepayment, withdrawalPosition
} from "./payrollFinanceCalculator";

const C = {
  employees: "payroll_fin_employees",
  withdrawals: "payroll_fin_withdrawals",
  movements: "payroll_fin_movements",
  repayments: "payroll_fin_repayments",
  uses: "payroll_fin_uses"
};

export const PF_ALLOWED_EMAILS = ["admin@almhr.com", "nazim@almhr.com"];
export const canUsePayrollFinancing = () => PF_ALLOWED_EMAILS.includes(auth.currentUser?.email?.toLowerCase() || "");

const text = (value) => String(value ?? "").trim();
const safeId = (value) => text(value).replace(/[^a-zA-Z0-9_-]+/g, "_");
export const withdrawalDocId = (bankTxnId) => `WD_${safeId(bankTxnId)}`;
const today = () => new Date().toISOString().slice(0, 10);
const actor = () => ({ uid: auth.currentUser?.uid || null, email: auth.currentUser?.email || null });
const created = () => ({ seq: Date.now(), entryStatus: "ACTIVE", createdBy: actor().uid, createdByEmail: actor().email, createdAt: serverTimestamp() });

function assertPortal(portal) {
  if (!PF_PORTALS.some((item) => item.code === portal)) throw new Error("Choose the ALM, CBA or DULA portal.");
}
function assertDate(value, label = "date") {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text(value))) throw new Error(`Enter the ${label}.`);
}

export async function loadAll() {
  const read = async (name) => (await getDocs(collection(db, name))).docs.map((item) => ({ id: item.id, ...item.data() }));
  const [employees, withdrawals, movements, repayments, uses] = await Promise.all(
    [C.employees, C.withdrawals, C.movements, C.repayments, C.uses].map(read)
  );
  return { employees, withdrawals, movements, repayments, uses };
}

function employeeIdOf({ cnic, name, account }) {
  const key = text(cnic).replace(/\D/g, "") || `${text(name)}_${text(account)}`;
  return `emp_${safeId(key)}`;
}

function employeeFields(input) {
  return {
    name: text(input.employeeName ?? input.name),
    cnic: text(input.employeeCnic ?? input.cnic),
    bank: text(input.employeeBank ?? input.bank),
    account: text(input.employeeAccount ?? input.account),
    phone: text(input.employeePhone ?? input.phone)
  };
}

export async function upsertEmployee(input) {
  const fields = employeeFields(input);
  if (!fields.name) throw new Error("Enter the employee name.");
  const id = employeeIdOf(fields);
  await setDoc(doc(db, C.employees, id), { ...fields, updatedAt: serverTimestamp(), updatedByEmail: actor().email }, { merge: true });
  return { id, ...fields };
}

function withdrawalFields(input, employeeId) {
  assertPortal(input.portal);
  assertDate(input.date, "withdrawal date");
  const amount = round2(input.amount);
  if (!(amount > 0)) throw new Error("Enter a withdrawal amount greater than zero.");
  const employee = employeeFields(input);
  if (!employee.name) throw new Error("Enter the employee who received the transfer.");
  const rate = input.monthlyRate === "" || input.monthlyRate == null ? DEFAULT_MONTHLY_RATE : Number(input.monthlyRate);
  if (!(rate > 0 && rate < 1)) throw new Error("The monthly rate must be between 0 and 100%.");
  return {
    portal: input.portal, date: input.date, time: text(input.time), amount, monthlyRate: rate,
    bankTxnId: text(input.bankTxnId), note: text(input.note),
    employeeId, employeeName: employee.name, employeeCnic: employee.cnic, employeeBank: employee.bank,
    employeeAccount: employee.account, employeePhone: employee.phone,
    bankInvoice: input.bankInvoice || null,
    source: input.source || "MANUAL"
  };
}

export async function createWithdrawal(input) {
  const employee = await upsertEmployee(input);
  const fields = withdrawalFields(input, employee.id);
  const ref = fields.bankTxnId ? doc(db, C.withdrawals, withdrawalDocId(fields.bankTxnId)) : doc(collection(db, C.withdrawals));
  if (fields.bankTxnId && (await getDoc(ref)).exists()) throw new Error(`Bank transaction ${fields.bankTxnId} is already recorded.`);
  await setDoc(ref, { ...fields, ...created() });
  return { id: ref.id };
}

// Creates only withdrawals whose bank transaction id is new; re-uploading the
// same sheet is therefore harmless.
export async function importWithdrawals({ portal, rows }) {
  assertPortal(portal);
  const existing = new Set((await getDocs(collection(db, C.withdrawals))).docs.map((item) => item.id));
  const fresh = rows.filter((row) => !existing.has(withdrawalDocId(row.bankTxnId)));
  let batch = writeBatch(db);
  let ops = 0;
  const flush = async () => { if (ops) { await batch.commit(); batch = writeBatch(db); ops = 0; } };
  for (const row of fresh) {
    const employeeId = employeeIdOf({ cnic: row.employeeCnic, name: row.employeeName, account: row.employeeAccount });
    batch.set(doc(db, C.employees, employeeId), { ...employeeFields(row), updatedAt: serverTimestamp(), updatedByEmail: actor().email }, { merge: true });
    const fields = withdrawalFields({ ...row, portal, monthlyRate: row.monthlyRate ?? DEFAULT_MONTHLY_RATE, source: "BANK_SHEET" }, employeeId);
    batch.set(doc(db, C.withdrawals, withdrawalDocId(row.bankTxnId)), { ...fields, ...created() });
    ops += 2;
    if (ops >= 400) await flush();
  }
  await flush();
  return { imported: fresh.length, skipped: rows.length - fresh.length };
}

function positionsFor(data, portal, asOf = today()) {
  const movements = data.movements.filter((m) => isActive(m) && m.portal === portal);
  const repayments = data.repayments.filter((r) => isActive(r) && r.portal === portal);
  return data.withdrawals.filter((w) => isActive(w) && w.portal === portal)
    .map((withdrawal) => withdrawalPosition({ withdrawal, movements, repayments, asOf }))
    .sort((a, b) => `${a.withdrawal.date} ${a.withdrawal.time || ""}`.localeCompare(`${b.withdrawal.date} ${b.withdrawal.time || ""}`));
}

async function postBorrowingCost({ amount, date, description, reference, note }) {
  const gross = Math.round(Number(amount || 0));
  if (!(gross > 0)) return { warning: "Under PKR 1 — not posted to Operating Expenses." };
  const { id } = await createExpense({
    category: "BORROWING_COST_EXPENSE", accountingPeriod: date.slice(0, 7), expenseDate: date,
    description, vendor: "Payroll financing partner bank", referenceNumber: reference || "",
    note: `${note || ""} (exact ${round2(amount)}; auto-posted from Payroll Financing)`.trim(),
    grossAmount: gross, amountBeforeTax: gross, taxAmount: 0
  });
  return { expenseId: id };
}

export async function recordMovement(input) {
  const type = input.type;
  if (!Object.values(MOVEMENT_TYPES).includes(type)) throw new Error("Choose a transfer to ALM or a charge.");
  assertPortal(input.portal);
  assertDate(input.date, "date");
  const amount = round2(input.amount);
  if (!input.employeeId) throw new Error("Choose the employee account.");
  if (type === MOVEMENT_TYPES.TO_ALM && !text(input.companyAccount)) throw new Error("Enter the ALM company account that received the money.");
  if (type === MOVEMENT_TYPES.CHARGE && !text(input.chargeType)) throw new Error("Enter what the charge was for.");

  const data = await loadAll();
  const positions = positionsFor(data, input.portal).filter((p) => p.withdrawal.employeeId === input.employeeId);
  const allocations = input.allocations?.length
    ? input.allocations.map((a) => ({ withdrawalId: a.withdrawalId, amount: round2(a.amount) }))
    : allocateFifo(positions.map((p) => ({ id: p.withdrawal.id, open: p.withEmployee })), amount).allocations;
  const problem = validateMovementAllocations({ amount, allocations, positions, type });
  if (problem) throw new Error(problem);

  const fields = {
    type, portal: input.portal, employeeId: input.employeeId,
    employeeName: positions[0]?.withdrawal.employeeName || "",
    date: input.date, time: text(input.time), amount, allocations,
    companyAccount: type === MOVEMENT_TYPES.TO_ALM ? text(input.companyAccount) : "",
    chargeType: type === MOVEMENT_TYPES.CHARGE ? text(input.chargeType) : "",
    reference: text(input.reference), note: text(input.note)
  };
  const ref = await addDoc(collection(db, C.movements), { ...fields, ...created() });
  let warning = "";
  if (type === MOVEMENT_TYPES.CHARGE) {
    try {
      const posted = await postBorrowingCost({
        amount, date: input.date, reference: fields.reference, note: fields.note,
        description: `Payroll financing charge — ${fields.chargeType} (${portalLabel(input.portal)}, ${fields.employeeName})`
      });
      if (posted.expenseId) await updateDoc(ref, { expenseId: posted.expenseId });
      warning = posted.warning || "";
    } catch (error) {
      warning = `Saved, but it could not be posted to Operating Expenses: ${error.message}`;
    }
  }
  return { id: ref.id, allocations, warning };
}

async function rejectLinked(row, reason) {
  if (!row.expenseId) return;
  try { await rejectExpense(row.expenseId, `Linked payroll-financing entry rejected: ${reason}`); } catch (error) { /* expense may already be handled */ }
}

async function rejectDoc(collectionName, id, reason, loadRow) {
  if (!text(reason)) throw new Error("A reason is required to reject an entry.");
  await updateDoc(doc(db, collectionName, id), {
    entryStatus: "REJECTED", rejectedReason: text(reason), rejectedByEmail: actor().email, rejectedAt: serverTimestamp()
  });
  if (loadRow) await rejectLinked(loadRow, reason);
}

export const rejectMovement = (row, reason) => rejectDoc(C.movements, row.id, reason, row);
export const rejectUse = (row, reason) => rejectDoc(C.uses, row.id, reason, null);

export async function rejectWithdrawal(row, reason) {
  const data = await loadAll();
  const used = [...data.movements, ...data.repayments].some((entry) => isActive(entry) && (entry.allocations || []).some((a) => a.withdrawalId === row.id));
  if (used) throw new Error("This withdrawal has transfers, charges or repayments against it. Reject those first.");
  await rejectDoc(C.withdrawals, row.id, reason, null);
}

export async function recordRepayment(input) {
  assertPortal(input.portal);
  assertDate(input.date, "repayment date");
  const data = await loadAll();
  const positions = positionsFor(data, input.portal, input.date);
  const allocations = (input.allocations || [])
    .map((a) => ({ withdrawalId: a.withdrawalId, principal: round2(a.principal), interest: Math.round(Number(a.interest || 0) * 10) / 10 }))
    .filter((a) => a.principal > 0 || a.interest > 0);
  const problem = validateRepayment({ allocations, positions, repayDate: input.date });
  if (problem) throw new Error(problem);

  const principalTotal = round2(allocations.reduce((sum, a) => sum + a.principal, 0));
  const interestTotal = Math.round(allocations.reduce((sum, a) => sum + a.interest, 0) * 10) / 10;
  const ref = await addDoc(collection(db, C.repayments), {
    portal: input.portal, date: input.date, time: text(input.time), allocations,
    principalTotal, interestTotal, amount: round2(principalTotal + interestTotal),
    reference: text(input.reference), note: text(input.note), ...created()
  });
  let warning = "";
  try {
    const posted = await postBorrowingCost({
      amount: interestTotal, date: input.date, reference: text(input.reference), note: text(input.note),
      description: `Interest on payroll financing — ${portalLabel(input.portal)}, repaid ${input.date}`
    });
    if (posted.expenseId) await updateDoc(ref, { expenseId: posted.expenseId });
    warning = posted.warning || "";
  } catch (error) {
    warning = `Saved, but the interest could not be posted to Operating Expenses: ${error.message}`;
  }
  return { id: ref.id, principalTotal, interestTotal, warning };
}

export const rejectRepayment = (row, reason) => rejectDoc(C.repayments, row.id, reason, row);

export async function recordUse(input) {
  if (input.portal) assertPortal(input.portal);
  assertDate(input.date, "date");
  const amount = round2(input.amount);
  if (!(amount > 0)) throw new Error("Enter an amount greater than zero.");
  if (!text(input.purpose)) throw new Error("Describe what the funds were used for.");
  if (!text(input.companyAccount)) throw new Error("Enter the ALM account the funds were paid from.");
  const ref = await addDoc(collection(db, C.uses), {
    portal: input.portal || "", date: input.date, time: text(input.time), amount,
    companyAccount: text(input.companyAccount), purpose: text(input.purpose), payee: text(input.payee),
    reference: text(input.reference), note: text(input.note), ...created()
  });
  return { id: ref.id };
}
